import { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CartContext from '../../contexts/CartContext';
import UserContext from '../../contexts/UserContext';
import apiClient from '../../utils/api-client';
import useFacebookPixel from '../../utils/useFacebookPixel';
import PageHeader from '../Common/PageHeader';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const { setUser, user } = useContext(UserContext);
  const { cart, clearCart } = useContext(CartContext);

  const userID = localStorage.getItem('userID');
  const userEmail = localStorage.getItem('userEmail');
  const facebookPixel = useFacebookPixel();
  const navigate = useNavigate();
  const location = useLocation();

  const [subTotal, setSubTotal] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [defaultShippingAddress, setDefaultShippingAddress] = useState([]);
  const [promo_code, setPromo_code] = useState('');
  const [promoData, setPromoData] = useState(null);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState('');
  const [toastShown, setToastShown] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  // ✅ Fetch Default Shipping Address
  useEffect(() => {
    if (!userID) return;
    const fetchAddress = async () => {
      try {
        const res = await apiClient.get(`/default-shipping-address/${userID}`);
        setDefaultShippingAddress(res.data?.data || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch shipping address');
      }
    };
    fetchAddress();
  }, [user, cities, userID]);

  // ✅ Fetch Cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await apiClient.get('/city');
        setCities(res.data?.data || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch cities');
      }
    };
    fetchCities();
  }, []);

  // ✅ Calculate Subtotal & Weight
  useEffect(() => {
    try {
      let total = 0;
      let weight = 0;
      cart.forEach((item) => {
        total += item?.product?.varients?.[0]?.final_price * item.quantity || 0;
        weight +=
          item?.product?.varients?.[0]?.product_weight * item.quantity || 0;
      });
      setSubTotal(total);
      setTotalWeight(weight);
    } catch (err) {
      console.error(err);
      setSubTotal(0);
      setTotalWeight(0);
    }
  }, [cart]);

  const totalOrderPrice = subTotal + shippingCharges;

  // ✅ Fetch Shipping Charges
  const getShippingCharges = async (weight) => {
    try {
      const response = await apiClient.post('/get-shipping-charges', {
        wc_weight: weight,
        sub_total: subTotal,
      });
      if(response.data?.status === false){
        toast.error(response.data?.message);
        return 0;
      }
      const charges = parseInt(response.data?.data?.wc_charges);
      if (isNaN(charges)) throw new Error('Invalid shipping charges');
      return charges;
    } catch (err) {
      console.error(err);
      return 0;
    }
  };

  useEffect(() => {
    const fetchCharges = async () => {
      try {
        const charges = await getShippingCharges(totalWeight);
        setShippingCharges(charges);
      } catch (err) {
        console.error(err);
        toast.error('Failed to get shipping charges');
      }
    };
    fetchCharges();
  }, [user, defaultShippingAddress, watch('city'), totalWeight, subTotal]);

  // ✅ Facebook Pixel
  const trackPurchase = (value, content_ids) => {
    try {
      if (facebookPixel) {
        facebookPixel.trackEvent('Purchase', {
          content_ids,
          content_type: 'product',
          value,
          currency: 'PKR',
        });
      }
    } catch (err) {
      console.error('Pixel error:', err);
    }
  };

  // ✅ Submit Checkout
  const onSubmit = (data) => {
    
      let formData;
      if (user) {
        formData = {
          full_name: defaultShippingAddress?.receiver_name || '',
          phone: defaultShippingAddress?.receiver_phone || '',
          city_id_fk: defaultShippingAddress?.city_id_fk || '',
          address: defaultShippingAddress?.street_address || '',
          email: userEmail || '',
          order_instructions: data.order_notes || '',
          rider_instructions: data.rider_notes || '',
          products: cart.map(({ product, variant, quantity }) => ({
            product_id: product?.product_id,
            variant_id: variant?.p_v_id,
            color_id_fk: variant?.color_id_fk,
            variant_price: variant?.final_price,
            quantity,
          })),
          order_price: subTotal,
          transaction_amount: discountedTotal.toFixed(0),
          shipping_charges: shippingCharges,
          discounted_value: savings.toFixed(0),
        };
      } else {
        formData = {
          full_name: data.full_name,
          phone: data.phone,
          city_id_fk: data.city,
          address: data.address,
          email: data.email,
          order_instructions: data.order_notes || '',
          rider_instructions: data.rider_notes || '',
          products: cart.map(({ product, variant, quantity }) => ({
            product_id: product?.product_id,
            variant_id: variant?.p_v_id,
            color_id_fk: variant?.color_id_fk,
            variant_price: variant?.final_price,
            quantity,
          })),
          order_price: subTotal,
          transaction_amount: discountedTotal,
          shipping_charges: shippingCharges,
          discounted_value: savings,
        };
      }

      setIsPlacingOrder(true);
       apiClient.post('/checkout', formData).then(response => {
        if (response.data?.status === true) {
          // localStorage.setItem(
          //   'userData',
          //   JSON.stringify(response.data?.userData)
          // );
          // localStorage.setItem('userID', response.data?.userData?.user_id);
          // localStorage.setItem('userEmail', response.data?.userData?.email);
          // setUser(response.data?.userData?.user_id);

          const content_ids = cart.map(({ product }) => product?.product_id);
          const totalValue = cart.reduce(
            (acc, { product, quantity }) =>
              acc + (product?.varients?.[0]?.final_price || 0) * quantity,
            0
          );

          trackPurchase(totalValue, content_ids);

          toast.success('Order placed successfully!');
          clearCart();
          navigate('/account/orders-history');
        } else {
          toast.error(response.data?.message || 'Checkout failed');
          if (response.data?.data) {
            response.data.data.forEach((err) => toast.error(err));
          }
        }
       }).catch(error => {
        //  toast.error('An error occurred while placing the order');
       }).finally(() => {
         setIsPlacingOrder(false);

       }) ;

      
    
  };

  // ✅ Apply Promo
  const promoSubmit = async () => {
    try {
      if (!promo_code) {
        toast.error('Please enter a promo code');
        return;
      }
      const response = await apiClient.post('/promocode', { promo_code });
      if (response.data?.status === true) {
        setPromoData(response.data?.data);
      } else {
        toast.error('Invalid promo code');
        setPromoData(null);
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while applying promo code');
    }
  };

  // ✅ Discount & Savings
  const discountedTotal =
    promoData && subTotal >= promoData?.order_value
      ? promoData?.is_percentage
        ? shippingCharges +
          subTotal -
          (subTotal * promoData.discounted_value) / 100
        : shippingCharges + subTotal - promoData.discounted_value
      : +totalOrderPrice;

  const savings =
    promoData && subTotal >= promoData?.order_value
      ? promoData?.is_percentage
        ? (subTotal * promoData.discounted_value) / 100
        : promoData.discounted_value
      : 0;

  // ✅ Promo Toasts
  useEffect(() => {
    try {
      if (promoData && subTotal < promoData?.order_value && !toastShown) {
        const amountRequired = promoData.order_value - subTotal;
        toast.info(`Add Rs.${amountRequired} more to apply the promo code.`);
        setToastShown(true);
      } else if (
        promoData &&
        subTotal >= promoData?.order_value &&
        !toastShown
      ) {
        toast.success(
          `Promo code applied successfully. You saved Rs.${savings.toFixed(0)}`
        );
        setToastShown(true);
      }
    } catch (err) {
      console.error(err);
    }
  }, [promoData, subTotal, savings, toastShown]);

  const defaultCity = defaultShippingAddress
    ? cities.find((city) => city.city_id === defaultShippingAddress.city_id_fk)
    : null;

  return (
    <>
      <Helmet>
        <title>
          Just Check out and Get your Unique Home Products on Doorstep
        </title>
        <meta
          name="keywords"
          content="new unique items,creative items for home,creative home decor items,creative decorative items,creative products to sell online,creative memories items for sale,creative gift supplies,creative item for home and office,unique products for home,unique home products,unique office wall decor,interesting home products,unique decorative pieces,unique home essentials,funky home decor items,unique home decorative items,unique home office decor,unique household gadgets"
        />
        <meta
          name="description"
          content="Don’t Get Late and Checkout Your Bill to get Unique Home Products. Our Delivery services are fast and quick that you don’t have to wait for a long time."
        />
      </Helmet>

      <PageHeader title="Checkout" />
      <div className="section py-4">
        <div className="container-fluid">
          <form onSubmit={handleSubmit(onSubmit)}>
            {!user && (
              <div className="row mb-5">
                <div className="col-md-12">
                  <div
                    className="border p-3 rounded"
                    style={{ backgroundColor: '#e8f7fb' }}
                  >
                    Returning customer?
                    <Link
                      to="/login"
                      state={{ from: location.pathname }}
                      className="text-decoration-underline text-purple ms-2"
                    >
                      Click here
                    </Link>{' '}
                    to login
                  </div>
                </div>
              </div>
            )}

            <div className="row g-4">
              {/* Billing Details */}
              <div className="col-lg-6">
                <h3 className="mb-2 text-black">Billing Details</h3>
                <div className="p-4 shadow-sm rounded bg-white">
                  {/* Full Name + Phone */}
                  <div className="row g-3 mb-2">
                    <div className="col-md-6">
                      <label
                        htmlFor="full_name"
                        className="form-label fw-semibold"
                      >
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        className="form-control"
                        {...(user && {
                          value: defaultShippingAddress.receiver_name,
                        })}
                        readOnly={user}
                        {...register('full_name', { required: !user })}
                      />
                      {errors.full_name && (
                        <small className="text-danger">
                          Full name is required
                        </small>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label fw-semibold">
                        Phone
                      </label>
                      <input
                        type="text"
                        id="phone"
                        className="form-control"
                        {...(user && {
                          value: defaultShippingAddress.receiver_phone,
                        })}
                        readOnly={user}
                        {...register('phone', {
                          required: !user,
                          pattern: /^03\d{9}$/,
                        })}
                      />
                      {errors.phone && (
                        <small className="text-danger">
                          Phone is required, Start with 03
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-2">
                    <label htmlFor="email" className="form-label fw-semibold">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      {...(user && { value: userEmail })}
                      readOnly={user}
                      {...register('email', {
                        required: !user,
                        pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      })}
                    />
                    {!user && errors.email && (
                      <small className="text-danger">Email is required</small>
                    )}
                  </div>

                  {/* City + Address (only if guest) */}
                  {!user && (
                    <>
                      <div className="mb-2">
                        <label
                          htmlFor="city"
                          className="form-label fw-semibold"
                        >
                          Town/City
                        </label>
                        <select
                          id="city"
                          className="form-select"
                          {...(user && { readOnly: true })}
                          {...(user &&
                            defaultCity && { value: defaultCity.city_id })}
                          {...register('city', { required: !user })}
                        >
                          <option value="">Select City</option>
                          {cities?.map((city) => (
                            <option key={city.city_id} value={city.city_id}>
                              {city.city_name}
                            </option>
                          ))}
                        </select>
                        {errors.city && (
                          <small className="text-danger">
                            City is required
                          </small>
                        )}
                      </div>
                      <div className="mb-2">
                        <label
                          htmlFor="address"
                          className="form-label fw-semibold"
                        >
                          Address
                        </label>
                        <textarea
                          id="address"
                          rows="3"
                          className="form-control"
                          {...(user && {
                            value: defaultShippingAddress.street_address,
                          })}
                          readOnly={user}
                          {...register('address', { required: !user })}
                        />
                        {errors.address && (
                          <small className="text-danger">
                            Address is required
                          </small>
                        )}
                      </div>
                    </>
                  )}

                  {/* Saved Address (if logged in) */}
                  {user && (
                    <div className="card border-0 bg-light mb-2">
                      <div className="card-body p-3">
                        <p className="mb-2 text-muted">
                          <i className="fa-solid fa-location-dot me-2 text-purple" />
                          Shipping Address
                        </p>
                        <p className="mb-2">
                          {defaultShippingAddress.street_address},{' '}
                          {defaultCity ? defaultCity.city_name : ''}
                        </p>
                        <p className="mb-0">
                          <strong>
                            {defaultShippingAddress.receiver_name}
                          </strong>{' '}
                          – {defaultShippingAddress.receiver_phone}
                        </p>
                        <Link
                          to="/account/profile"
                          className="btn btn-sm mt-3 text-white"
                          style={{ backgroundColor: '#791a89' }}
                        >
                          Change Address
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div className="mb-3">
                    <label
                      htmlFor="order_notes"
                      className="form-label fw-semibold"
                    >
                      Instructions for the order{' '}
                      <span className="text-muted">(optional)</span>
                    </label>
                    <textarea
                      id="order_notes"
                      rows="3"
                      className="form-control"
                      placeholder="E.g., Please pack items separately or add extra napkins"
                      {...register('order_notes')}
                    />
                  </div>

                  <div className="mb-0">
                    <label
                      htmlFor="rider_notes"
                      className="form-label fw-semibold"
                    >
                      Instructions for the rider{' '}
                      <span className="text-muted">(optional)</span>
                    </label>
                    <textarea
                      id="rider_notes"
                      rows="4"
                      className="form-control"
                      placeholder="E.g., Call upon arrival or leave at the doorstep"
                      {...register('rider_notes')}
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar: Coupon + Order Summary */}
              <div className="col-lg-6">
                {/* Coupon */}
                <div className="mb-2">
                  <h3 className="h3 mb-2 text-black">Coupon Code</h3>
                  <div className="p-3 rounded bg-white shadow-sm">
                    <label htmlFor="c_code" className="form-label fw-semibold">
                      Enter your coupon code if you have one
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        id="c_code"
                        className="form-control"
                        placeholder="Coupon Code"
                        value={promo_code}
                        onChange={(e) => setPromo_code(e.target.value)}
                      />
                      <button
                        className="btn btn-primary bg-purple border-0"
                        type="button"
                        onClick={promoSubmit}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div>
                  <h3 className="h3 mb-2 text-black">Your Order</h3>
                  <div className="p-3 shadow-sm rounded bg-white">
                    <table className="table mb-4">
                      <thead>
                        <tr>
                          <th>Products</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart?.map(({ product, quantity, variant }) => (
                          <tr key={product.product_id}>
                            <td>
                              {product.product_name} –{' '}
                              <span className="text-purple fw-semibold">
                                {variant.p_v_name}
                              </span>{' '}
                              × {quantity}
                            </td>
                            <td className="text-end">
                              Rs {product.varients[0].final_price * quantity}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td className="fw-semibold">Cart Subtotal</td>
                          <td className="text-end">Rs {subTotal}</td>
                        </tr>
                        <tr>
                          <td className="fw-semibold">Shipping Charges</td>
                          <td className="text-end">Rs {shippingCharges}</td>
                        </tr>
                        {promoData && (
                          <tr>
                            <td className="fw-semibold text-success">
                              You Saved
                            </td>
                            <td className="text-end text-success">
                              Rs {savings.toFixed(0)}
                            </td>
                          </tr>
                        )}
                        <tr className="table-light">
                          <td className="fw-bold text-purple">Order Summary</td>
                          <td className="fw-bold text-purple">
                            Rs:&nbsp;{discountedTotal.toFixed(0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <button
                      className="btn btn-primary border-0 btn-sm bg-purple w-100 "
                      disabled={isPlacingOrder}
                    >
                      {isPlacingOrder ? 'Order Placing...' : 'Place Order'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
export default CheckoutPage;
